import React, { useState, useMemo, useCallback } from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  TopToolbar,
  SearchInput,
  useGetOne,
} from 'react-admin';
import { Card, Typography, Box, IconButton, Skeleton } from '@mui/material';
import {
  Add as AddIcon,
  Input as InputIcon,
  TableChart as TableIcon,
  LocationOn as ZoneIcon,
  Factory as PlantIcon,
  Visibility as ShowIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import CustomDeleteButton from '../../components/CustomDeleteButton';
import CustomCreateButton from '@/components/CustomCreateButton';

// Estilo para truncar texto
const textOverflowStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const buttonStyle = {
  minWidth: '24px',
  width: '24px',
  height: '24px',
  padding: '2px',
  '& .MuiButton-startIcon': {
    margin: 0,
  },
  '& svg': {
    fontSize: '16px',
  },
};

// Componente para mostrar la tabla
const TableField = ({ record }: { record: any }) => {
  const tableId = record?.tableId;

  const {
    data: table,
    isLoading,
    error,
  } = useGetOne('tables', { id: tableId }, { enabled: !!tableId });

  if (!tableId) {
    return (
      <Box display='flex' alignItems='center' gap={0.5} sx={textOverflowStyle}>
        <TableIcon fontSize='small' color='disabled' />
        <span style={{ color: '#999' }}>Sin tabla</span>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display='flex' alignItems='center' gap={0.5}>
        <Skeleton variant='circular' width={16} height={16} />
        <Skeleton variant='text' width={80} height={20} />
      </Box>
    );
  }

  if (error || !table) {
    return (
      <Box display='flex' alignItems='center' gap={0.5}>
        <TableIcon fontSize='small' color='error' />
        <span style={{ color: '#f44336' }}>Error</span>
      </Box>
    );
  }

  return (
    <Box display='flex' alignItems='center' gap={0.5} sx={textOverflowStyle}>
      <TableIcon fontSize='small' color='primary' />
      <span>{table.tableName || table.name || `Tabla ${table.id}`}</span>
    </Box>
  );
};

// Componente para mostrar la zona
const ZoneField = ({ record }: { record: any }) => {
  const tableId = record?.tableId;

  // Obtener la tabla primero
  const {
    data: table,
    isLoading: isLoadingTable,
    error: tableError,
  } = useGetOne('tables', { id: tableId }, { enabled: !!tableId });

  // Obtener la zona
  const {
    data: zone,
    isLoading: isLoadingZone,
    error: zoneError,
  } = useGetOne('zones', { id: table?.zoneId }, { enabled: !!table?.zoneId });

  const isLoading = isLoadingTable || isLoadingZone;
  const hasError = tableError || zoneError;

  if (!tableId) {
    return (
      <Box display='flex' alignItems='center' gap={0.5} sx={textOverflowStyle}>
        <ZoneIcon fontSize='small' color='disabled' />
        <span style={{ color: '#999' }}>Sin zona</span>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display='flex' alignItems='center' gap={0.5}>
        <Skeleton variant='circular' width={16} height={16} />
        <Skeleton variant='text' width={80} height={20} />
      </Box>
    );
  }

  if (hasError || !zone) {
    return (
      <Box display='flex' alignItems='center' gap={0.5}>
        <ZoneIcon fontSize='small' color='error' />
        <span style={{ color: '#f44336' }}>Error</span>
      </Box>
    );
  }

  return (
    <Box display='flex' alignItems='center' gap={0.5} sx={textOverflowStyle}>
      <ZoneIcon fontSize='small' color='primary' />
      <span>{zone.zoneName || zone.name || `Zona ${zone.id}`}</span>
    </Box>
  );
};

// Componente para mostrar la planta
const PlantField = ({ record }: { record: any }) => {
  const tableId = record?.tableId;

  // Obtener la tabla primero
  const {
    data: table,
    isLoading: isLoadingTable,
    error: tableError,
  } = useGetOne('tables', { id: tableId }, { enabled: !!tableId });

  // Obtener la zona
  const {
    data: zone,
    isLoading: isLoadingZone,
    error: zoneError,
  } = useGetOne('zones', { id: table?.zoneId }, { enabled: !!table?.zoneId });

  // Obtener la planta
  const {
    data: plant,
    isLoading: isLoadingPlant,
    error: plantError,
  } = useGetOne('plants', { id: zone?.plantId }, { enabled: !!zone?.plantId });

  const isLoading = isLoadingTable || isLoadingZone || isLoadingPlant;
  const hasError = tableError || zoneError || plantError;

  if (!tableId) {
    return (
      <Box display='flex' alignItems='center' gap={0.5} sx={textOverflowStyle}>
        <PlantIcon fontSize='small' color='disabled' />
        <span style={{ color: '#999' }}>Sin planta</span>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display='flex' alignItems='center' gap={0.5}>
        <Skeleton variant='circular' width={16} height={16} />
        <Skeleton variant='text' width={80} height={20} />
      </Box>
    );
  }

  if (hasError || !plant) {
    return (
      <Box display='flex' alignItems='center' gap={0.5}>
        <PlantIcon fontSize='small' color='error' />
        <span style={{ color: '#f44336' }}>Error</span>
      </Box>
    );
  }

  return (
    <Box display='flex' alignItems='center' gap={0.5} sx={textOverflowStyle}>
      <PlantIcon fontSize='small' color='primary' />
      <span>{plant.plantName || plant.name || `Planta ${plant.id}`}</span>
    </Box>
  );
};

const InputListActions = () => (
  <TopToolbar>
    <CustomCreateButton
      variant='contained'
      label='Crear Input'
      icon={<AddIcon />}
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    />
  </TopToolbar>
);

// Filtros de búsqueda
const inputFilters = [
  <SearchInput
    key='search'
    source='q'
    placeholder='Buscar inputs...'
    alwaysOn
    size='small'
    sx={{
      minWidth: 200,
      mb: 0.5,
    }}
  />,
];

export const InputsList = () => {
  const [filterValues, setFilterValues] =
    useState<HierarchicalFilterSelections>({});
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });

  const handleFilterChange = useCallback(
    (selections: HierarchicalFilterSelections) => {
      console.log(
        '🎯 [InputList] Filtros cambiados en HierarchicalFilter:',
        selections
      );
      setFilterValues(selections);
      setPagination(prev => ({ ...prev, page: 1 }));
    },
    []
  );

  // Filter object por React Admin -
  const filter = useMemo(() => {
    const filterObj: any = {};

    // Solo incluir filtros si tienen valores
    if (filterValues.plantId) {
      filterObj.plantId = filterValues.plantId;
      console.log(
        '✅ [InputList] Aplicando filtro plantId:',
        filterValues.plantId
      );
    }

    if (filterValues.zoneId) {
      filterObj.zoneId = filterValues.zoneId;
      console.log(
        '✅ [InputList] Aplicando filtro zoneId:',
        filterValues.zoneId
      );
    }

    if (filterValues.tableId) {
      filterObj.tableId = filterValues.tableId;
      console.log(
        '✅ [InputList] Aplicando filtro tableId:',
        filterValues.tableId
      );
    }

    // El search string se maneja automáticamente con el source='q'
    // PAGINACIÓN SERVIDOR
    filterObj.isPaginated = true;
    filterObj.page = pagination.page;
    filterObj.pageSize = pagination.perPage;

    console.log('🚀 [InputList] Enviando parámetros al backend:', filterObj);

    return filterObj;
  }, [
    filterValues.plantId,
    filterValues.zoneId,
    filterValues.tableId,
    pagination.page,
    pagination.perPage,
  ]);

  return (
    <Card elevation={1} sx={{ m: 2, p: 2 }}>
      <Box
        sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography
          variant='h5'
          component='h1'
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <InputIcon color='primary' />
          Gestión de Inputs
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Gestiona los inputs del sistema organizados por tablas y zonas
        </Typography>
      </Box>

      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant='h6' gutterBottom color='primary'>
          Filtros de Búsqueda
        </Typography>

        {/* Filtro jerárquico  */}
        <HierarchicalFilter
          levels={['plant', 'zone', 'table']}
          onChange={handleFilterChange}
          size='medium'
          showTable={true}
          useTableSelector={true}
        />

        {/* Mostrar selección actual */}
        {(filterValues.plantId ||
          filterValues.zoneId ||
          filterValues.tableId) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              Selección actual:
              {filterValues.plantName && ` Planta: ${filterValues.plantName}`}
              {filterValues.zoneName && ` → Zona: ${filterValues.zoneName}`}
              {filterValues.tableName && ` → Tabla: ${filterValues.tableName}`}
            </Typography>
            {filterValues.tableId && (
              <Typography variant='body2' color='success.main' sx={{ mt: 1 }}>
                ✓ Filtro aplicado: Mostrando inputs de la tabla seleccionada
              </Typography>
            )}
          </Box>
        )}

        {/* Indicador cuando no hay filtros */}
        {!filterValues.plantId &&
          !filterValues.zoneId &&
          !filterValues.tableId && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' color='info.main'>
                ✓ Mostrando todos los inputs del sistema
              </Typography>
            </Box>
          )}
      </Box>

      {/* Lista de react-admin */}
      <List
        title={false}
        actions={<InputListActions />}
        filter={filter}
        filters={inputFilters}
        perPage={pagination.perPage}
        sort={{ field: 'variableName', order: 'ASC' }}
        exporter={false}
        sx={{
          '& .RaList-main': { mt: 0, p: 0 },
          '& .RaDatagrid-rowCell, & .RaDatagrid-headerCell': {
            padding: '6px 8px',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            pb: 1,
          }}
        >
          <Datagrid
            rowClick={false}
            sx={{
              '& .RaDatagrid-headerCell': {
                fontWeight: 'bold',
                padding: '8px 4px',
              },
              '& .RaDatagrid-rowCell': {
                padding: '8px 4px',
              },
              tableLayout: 'fixed',
              width: '100%',
            }}
          >
            {/* ID  */}
            <TextField
              source='id'
              label='ID'
              sx={{
                width: '40px',
                ...textOverflowStyle,
              }}
            />

            {/* Nombre */}
            <TextField
              source='variableName'
              label='Nombre'
              sx={{
                width: '140px',
                ...textOverflowStyle,
              }}
            />

            {/* DESCRIPCIÓN - MÁS ESTRECHA */}
            <TextField
              source='description'
              label='Descripción'
              sx={{
                width: '150px',
                display: 'block',
                overflow: 'hidden !important',
                textOverflow: 'ellipsis !important',
                whiteSpace: 'nowrap !important',
                maxWidth: '150px',
              }}
            />

            {/* Tabla  */}
            <FunctionField
              label='Tabla'
              render={(record: any) => <TableField record={record} />}
              sortable={false}
              sx={{
                width: '110px',
                ...textOverflowStyle,
              }}
            />

            {/* Zona  */}
            <FunctionField
              label='Zona'
              render={(record: any) => <ZoneField record={record} />}
              sortable={false}
              sx={{
                width: '100px',
                ...textOverflowStyle,
              }}
            />

            {/* Planta  */}
            <FunctionField
              label='Planta'
              render={(record: any) => <PlantField record={record} />}
              sortable={false}
              sx={{
                width: '100px',
                ...textOverflowStyle,
              }}
            />

            {/* Acciones */}
            <FunctionField
              label=''
              render={(record: any) => (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '4px',
                    justifyContent: 'center',
                    width: '80px',
                  }}
                >
                  <IconButton
                    size='small'
                    component={Link}
                    to={`/inputs/${record.id}/show`}
                    sx={buttonStyle}
                    color='primary'
                  >
                    <ShowIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    component={Link}
                    to={`/inputs/${record.id}`}
                    sx={buttonStyle}
                    color='secondary'
                  >
                    <EditIcon fontSize='small' />
                  </IconButton>
                  <CustomDeleteButton
                    record={record}
                    resource='inputs'
                    nameField='variableName'
                    sx={buttonStyle}
                    label={false}
                  />
                </Box>
              )}
              sx={{
                width: '80px',
                textAlign: 'center',
              }}
            />
          </Datagrid>
        </Box>
      </List>
    </Card>
  );
};

export default InputsList;
